import React, { useCallback, useMemo } from 'react';
import { noop, uniqueId } from 'lodash';
import cnx from 'classnames';
import { withParentSize } from '@visx/responsive';
import { localPoint } from '@visx/event';
import { TooltipWithBounds, useTooltip } from '@visx/tooltip';
import { faRectangleVertical } from '@fortawesome/pro-solid-svg-icons';
import { Icon } from 'js/components';

const GRID_TICKS = [0, 0.25, 0.5, 0.75];
const MAX_LABEL_LENGTH = 22;
const LABEL_RADIUS_OFFSET = 5;
const LABEL_FONT_SIZE = 9.6; // matches .radar-axis-label (0.6rem)
/** Minimum outer-arc length of one pie wedge before any axis labels are shown. */
export const MIN_WEDGE_ARC_FOR_LABELS = 100;
const LABEL_DY_UPPER = -0.35;
const LABEL_DY_LOWER = 0.85;
// Approximate glyph box relative to the text baseline (em).
const LABEL_ASCENT = 0.8;
const LABEL_DESCENT = 0.15;
const CONTENT_PADDING = 15;
const SCORE_DOMAIN_MIN = -0.2;
const SCORE_DOMAIN_MAX = 1;

export function scoreToRadius(value, radius) {
  const span = SCORE_DOMAIN_MAX - SCORE_DOMAIN_MIN;
  const t = ((value ?? 0) - SCORE_DOMAIN_MIN) / span;
  return Math.max(0, Math.min(1, t)) * radius;
}

function truncateLabel(text, max = MAX_LABEL_LENGTH) {
  const value = `${text ?? ''}`;
  return value.length > max ? `${value.slice(0, max - 1)}…` : value;
}

const START_ANGLE = -Math.PI / 2;

function polarPoint(cx, cy, radius, angle) {
  return {
    x: cx + radius * Math.cos(angle),
    y: cy + radius * Math.sin(angle),
  };
}

export function polygonPoints(cx, cy, radius, count, rotation = START_ANGLE) {
  const step = (2 * Math.PI) / Math.max(count, 1);
  return Array.from({ length: count }, (_, i) =>
    polarPoint(cx, cy, radius, rotation + step * i)
  );
}

export function pointsToPath(points, close = true) {
  if (!points.length) {
    return '';
  }
  const d = points
    .map((point, i) => `${i === 0 ? 'M' : 'L'}${point.x} ${point.y}`)
    .join(' ');
  return close ? `${d} Z` : d;
}

function axisAngle(index, count) {
  return START_ANGLE + ((2 * Math.PI) / Math.max(count, 1)) * index;
}

function isLowerHalf(angle) {
  return Math.sin(angle) > 0;
}

export function curvedLabelPath(cx, cy, radius, angle, halfSpan) {
  const reverse = isLowerHalf(angle);
  const startAngle = reverse ? angle + halfSpan : angle - halfSpan;
  const endAngle = reverse ? angle - halfSpan : angle + halfSpan;
  const start = polarPoint(cx, cy, radius, startAngle);
  const end = polarPoint(cx, cy, radius, endAngle);
  const sweep = reverse ? 0 : 1;
  return `M ${start.x} ${start.y} A ${radius} ${radius} 0 0 ${sweep} ${end.x} ${end.y}`;
}

export function labelArcHalfSpan(text, radius) {
  const width = Math.max(truncateLabel(text).length * 6.5, 24);
  return Math.min(Math.PI / 2, (width / 2 + 10) / Math.max(radius, 1));
}

/** Outer-arc length of one pie wedge at `radius` (independent of label text). */
export function pieWedgeArcLength(radius, axisCount) {
  return (2 * Math.PI * Math.max(radius, 0)) / Math.max(axisCount, 1);
}

/**
 * All axis labels shown or all hidden. Uses the pie-wedge outer arc, not the
 * estimated width of any label. Long labels are still truncated with an ellipsis
 * when the ring is drawn.
 */
export function radarAxisLabelsVisible(radius, axisCount) {
  return pieWedgeArcLength(radius, axisCount) >= MIN_WEDGE_ARC_FOR_LABELS;
}

function midpoint(a, b) {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

function cornerBoundaryPath(prev, vertex, next) {
  const start = midpoint(prev, vertex);
  const end = midpoint(vertex, next);
  return `M${start.x} ${start.y} L${vertex.x} ${vertex.y} L${end.x} ${end.y}`;
}

function circularSectorPath(cx, cy, radius, startAngle, endAngle) {
  const span = endAngle - startAngle;
  if (span >= 2 * Math.PI - 1e-6) {
    return [
      `M ${cx + radius} ${cy}`,
      `A ${radius} ${radius} 0 1 1 ${cx - radius} ${cy}`,
      `A ${radius} ${radius} 0 1 1 ${cx + radius} ${cy}`,
      'Z',
    ].join(' ');
  }
  const start = polarPoint(cx, cy, radius, startAngle);
  const end = polarPoint(cx, cy, radius, endAngle);
  const largeArc = span > Math.PI ? 1 : 0;
  return `M ${cx} ${cy} L ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArc} 1 ${end.x} ${end.y} Z`;
}

function consecutiveCategoryGroups(axes) {
  const groups = [];
  for (let i = 0; i < axes.length; ) {
    const id = axes[i].category ?? axes[i].key;
    let j = i;
    while (
      j + 1 < axes.length &&
      (axes[j + 1].category ?? axes[j + 1].key) === id
    ) {
      j += 1;
    }
    groups.push({ start: i, end: j, axis: axes[i] });
    i = j + 1;
  }
  return groups;
}

export function categoryBackgroundSectors(axes, cx, cy, radius) {
  const count = axes.length;
  if (count === 0) {
    return [];
  }

  const groups = consecutiveCategoryGroups(axes);
  const usePolygon = count >= 3;

  if (!usePolygon) {
    const step = (2 * Math.PI) / count;
    return groups.map((group) => {
      const startAngle = axisAngle(group.start, count) - step / 2;
      const endAngle = axisAngle(group.end, count) + step / 2;
      return {
        key: `${group.axis.category ?? group.axis.key}-fill`,
        paletteClassName: group.axis.paletteClassName,
        d: circularSectorPath(cx, cy, radius, startAngle, endAngle),
      };
    });
  }

  const outer = polygonPoints(cx, cy, radius, count);
  return groups.map((group) => {
    const { start, end, axis } = group;
    const points =
      groups.length === 1
        ? outer
        : [
            { x: cx, y: cy },
            midpoint(outer[(start - 1 + count) % count], outer[start]),
            ...outer.slice(start, end + 1),
            midpoint(outer[end], outer[(end + 1) % count]),
          ];
    return {
      key: `${axis.category ?? axis.key}-fill`,
      paletteClassName: axis.paletteClassName,
      d: pointsToPath(points, true),
    };
  });
}

const ACTIVE_MARKER = { rSpacer: 7, rOuter: 5, rInner: 3 };
const INACTIVE_MARKER = { rSpacer: 4.5, rOuter: 3, rInner: 1.75 };
const TOOLTIP_HIT_RADIUS = ACTIVE_MARKER.rSpacer;
const ACTIVE_PUNCH_SNEAK = 0.15;
const ACTIVE_PUNCH_MASK_FILL = `rgb(${ACTIVE_PUNCH_SNEAK * 100}%, ${
  ACTIVE_PUNCH_SNEAK * 100
}%, ${ACTIVE_PUNCH_SNEAK * 100}%)`;

/**
 * Bounding box of radar vertices, markers, and curved axis labels
 * in coordinates centered at the origin (before translate).
 *
 * Label text is centered on each arc (`startOffset="50%"`), so glyph
 * ascent/descent is applied at the arc midpoint using the same `dy` as the SVG.
 */
export function radarContentBounds(axes, radius, options = {}) {
  const axisCount = axes.length;
  if (axisCount === 0 || !(radius > 0)) {
    return { minX: 0, maxX: 0, minY: 0, maxY: 0 };
  }

  const labelRadius = radius + LABEL_RADIUS_OFFSET;
  const showLabels = radarAxisLabelsVisible(labelRadius, axisCount);
  const includeAxisLabel =
    options.includeAxisLabel ??
    ((axis) =>
      showLabels &&
      !(axis?.dummy || axis?.kind === 'dummy') &&
      Boolean(axis?.label));
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;

  const expand = (x, y) => {
    minX = Math.min(minX, x);
    maxX = Math.max(maxX, x);
    minY = Math.min(minY, y);
    maxY = Math.max(maxY, y);
  };

  for (let i = 0; i < axisCount; i++) {
    const angle = axisAngle(i, axisCount);
    const vertex = polarPoint(0, 0, radius, angle);
    expand(vertex.x, vertex.y);
    expand(vertex.x, vertex.y + ACTIVE_MARKER.rSpacer);
    expand(vertex.x, vertex.y - ACTIVE_MARKER.rSpacer);
    expand(vertex.x + ACTIVE_MARKER.rSpacer, vertex.y);
    expand(vertex.x - ACTIVE_MARKER.rSpacer, vertex.y);

    const axis = axes[i];
    if (!includeAxisLabel(axis)) {
      continue;
    }

    const halfSpan = labelArcHalfSpan(axis.label, labelRadius);
    const lower = isLowerHalf(angle);
    const textDy = (lower ? LABEL_DY_LOWER : LABEL_DY_UPPER) * LABEL_FONT_SIZE;

    // Arc ends affect horizontal space for side labels; text itself is centered.
    for (let s = -2; s <= 2; s += 1) {
      const sampleAngle = angle + (halfSpan * s) / 2;
      const point = polarPoint(0, 0, labelRadius, sampleAngle);
      minX = Math.min(minX, point.x);
      maxX = Math.max(maxX, point.x);
    }

    // Glyph box around the baseline at the arc midpoint (textPath center).
    const center = polarPoint(0, 0, labelRadius, angle);
    const baselineY = center.y + textDy;
    expand(center.x, baselineY - LABEL_ASCENT * LABEL_FONT_SIZE);
    expand(center.x, baselineY + LABEL_DESCENT * LABEL_FONT_SIZE);
  }

  return { minX, maxX, minY, maxY };
}

export function ringDonutPath(rOuter, rInner) {
  return [
    `M ${rOuter} 0`,
    `A ${rOuter} ${rOuter} 0 1 0 ${-rOuter} 0`,
    `A ${rOuter} ${rOuter} 0 1 0 ${rOuter} 0`,
    `M ${rInner} 0`,
    `A ${rInner} ${rInner} 0 1 1 ${-rInner} 0`,
    `A ${rInner} ${rInner} 0 1 1 ${rInner} 0`,
  ].join(' ');
}

function seriesPoints(values, cx, cy, radius, count) {
  return values.map((value, i) =>
    polarPoint(cx, cy, scoreToRadius(value, radius), axisAngle(i, count))
  );
}

function layerMaskRect(chartSize) {
  return {
    x: -12,
    y: -12,
    width: chartSize.width + 24,
    height: chartSize.height + 24,
  };
}

export function formatRadarPercent(value) {
  if (value == null || Number.isNaN(value)) {
    return null;
  }
  return `${Math.round(value * 100 + Number.EPSILON)}%`;
}

export function formatRadarRange(min, max) {
  const minPercent = formatRadarPercent(min);
  const maxPercent = formatRadarPercent(max);
  if (!minPercent || !maxPercent) {
    return null;
  }
  return `(${minPercent}-${maxPercent})`;
}

export function findClosestRadarMarker(
  markers,
  x,
  y,
  { maxDistance = Infinity } = {}
) {
  if (!markers.length) {
    return null;
  }
  const closest = markers.reduce((best, marker) => {
    const bestDist = (best.point.x - x) ** 2 + (best.point.y - y) ** 2;
    const candidateDist = (marker.point.x - x) ** 2 + (marker.point.y - y) ** 2;
    return candidateDist < bestDist ? marker : best;
  });
  const distance = Math.hypot(closest.point.x - x, closest.point.y - y);
  return distance <= maxDistance ? closest : null;
}

export function distanceToRadarSegment(px, py, start, end) {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const lengthSquared = dx * dx + dy * dy;
  if (lengthSquared === 0) {
    return Math.hypot(px - start.x, py - start.y);
  }
  const t = Math.max(
    0,
    Math.min(1, ((px - start.x) * dx + (py - start.y) * dy) / lengthSquared)
  );
  return Math.hypot(px - (start.x + t * dx), py - (start.y + t * dy));
}

export function radarSeriesSegments(points, close) {
  if (!points || points.length < 2) {
    return [];
  }
  const segments = [];
  for (let i = 0; i < points.length - 1; i += 1) {
    segments.push([points[i], points[i + 1]]);
  }
  if (close && points.length > 2) {
    segments.push([points[points.length - 1], points[0]]);
  }
  return segments;
}

export function findClosestRadarSegment(seriesItems, x, y) {
  let closest = null;
  seriesItems.forEach((item) => {
    radarSeriesSegments(item.points, item.points.length > 2).forEach(
      ([start, end]) => {
        const distance = distanceToRadarSegment(x, y, start, end);
        if (!closest || distance < closest.distance) {
          closest = { seriesKey: item.key, distance };
        }
      }
    );
  });
  return closest;
}

function axisKindLabel(axis) {
  if (axis?.kind === 'question') {
    return 'Question';
  }
  if (axis?.kind === 'questionSet') {
    return 'Question set';
  }
  if (axis?.kind === 'category') {
    return 'Category';
  }
  return null;
}

function isDummyAxis(axis) {
  return Boolean(axis?.dummy || axis?.kind === 'dummy');
}

function scoredMarkers(item, axes = []) {
  return item.points.flatMap((point, i) =>
    item.values[i] == null || isDummyAxis(axes[i])
      ? []
      : [
          {
            point,
            value: item.values[i],
            min: item.mins?.[i] ?? null,
            max: item.maxes?.[i] ?? null,
            axis: axes[i],
            seriesKey: item.key,
            seriesName: item.name,
          },
        ]
  );
}

function markerHoles(item, axes) {
  return scoredMarkers(item, axes).map((marker) => marker.point);
}

function RadarNodeTooltip({
  tooltipOpen,
  tooltipData,
  tooltipLeft,
  tooltipTop,
}) {
  const paletteClassName = useMemo(
    () => cnx('text-only', tooltipData?.axis?.paletteClassName),
    [tooltipData?.axis?.paletteClassName]
  );

  if (!tooltipOpen || !tooltipData) {
    return null;
  }

  const kind = axisKindLabel(tooltipData.axis);
  const label = tooltipData.axis?.detailLabel || tooltipData.axis?.label || '';
  const cohortName = tooltipData.seriesName;
  const score = formatRadarPercent(tooltipData.value);
  const range = formatRadarRange(tooltipData.min, tooltipData.max);

  return (
    <TooltipWithBounds
      left={tooltipLeft}
      top={tooltipTop}
      className="radar-node-tooltip"
    >
      {kind ? <div className="radar-node-tooltip-kind">{kind}</div> : null}
      {label ? (
        <div className="radar-node-tooltip-label">
          {paletteClassName ? (
            <span className={paletteClassName}>
              <Icon
                icon={faRectangleVertical}
                size="sm"
                className="palette-text radar-node-tooltip-swatch icon-left"
              />
            </span>
          ) : null}
          <span>{label}</span>
        </div>
      ) : null}
      {cohortName ? (
        <div className="radar-node-tooltip-cohort">{cohortName}</div>
      ) : null}
      {score ? (
        <div className="radar-node-tooltip-score">
          {score}
          {range ? ` ${range}` : ''}
        </div>
      ) : null}
    </TooltipWithBounds>
  );
}

function RadarSeriesItem({ item, axes, axisCount, chartSize }) {
  const d = pointsToPath(item.points, axisCount > 2);
  const marker = item.active ? ACTIVE_MARKER : INACTIVE_MARKER;
  const maskId = useMemo(() => uniqueId('radar-cohort-mask-'), []);
  const markers = scoredMarkers(item, axes);
  const maskRect = layerMaskRect(chartSize);

  return (
    <g
      className={cnx(item.paletteClassName, {
        'radar-series-item': true,
        'has-ink': Boolean(item.inkColor),
        active: item.active,
        inactive: !item.active,
      })}
      style={
        item.inkColor ? { '--radar-series-ink': item.inkColor } : undefined
      }
    >
      <defs>
        <mask
          id={maskId}
          maskUnits="userSpaceOnUse"
          maskContentUnits="userSpaceOnUse"
        >
          <rect {...maskRect} fill="white" />
          {markers.map((markerItem, i) => (
            <circle
              key={`${item.key}-hole-${i}`}
              cx={markerItem.point.x}
              cy={markerItem.point.y}
              r={marker.rSpacer}
              fill="black"
            />
          ))}
        </mask>
      </defs>
      <g mask={`url(#${maskId})`}>
        {axisCount > 2 && item.filled ? (
          <path d={d} className="radar-series-fill palette-fill" />
        ) : null}
        <path
          d={d}
          className="radar-series-stroke palette-stroke"
          fill="none"
        />
      </g>
      {markers.map((markerItem, i) => (
        <g
          key={`${item.key}-marker-${i}`}
          className="radar-series-marker"
          transform={`translate(${markerItem.point.x},${markerItem.point.y})`}
        >
          <path
            d={ringDonutPath(marker.rOuter, marker.rInner)}
            className="palette-fill"
            fillRule="evenodd"
          />
        </g>
      ))}
    </g>
  );
}

const RadarChart = ({
  parentWidth,
  margin,
  axes = [],
  series = [],
  activeSeriesKey = null,
  filledSeriesKey = null,
  lockedSeriesKey = null,
  dimInactive = false,
  onHoverSeries = noop,
  onClearSeries = noop,
  onClickSeries = noop,
}) => {
  const labeledSideMargin = 48 + LABEL_RADIUS_OFFSET;
  const labeledMargins = {
    top: CONTENT_PADDING,
    right: labeledSideMargin,
    bottom: CONTENT_PADDING,
    left: labeledSideMargin,
    ...margin,
  };
  const chartWidth = Math.max(parentWidth || 0, 1);
  const labeledWidth = Math.max(
    chartWidth - (labeledMargins.left + labeledMargins.right),
    1
  );
  const labeledRadius = Math.max(labeledWidth / 2, 1);
  const labeledLabelRadius = labeledRadius + LABEL_RADIUS_OFFSET;
  const axisCount = axes.length;
  const labelsFitWedge = radarAxisLabelsVisible(labeledLabelRadius, axisCount);
  const showAxisLabel = (axis) =>
    labelsFitWedge && !isDummyAxis(axis) && Boolean(axis?.label);
  const anyLabelsVisible = axes.some(showAxisLabel);
  const margins = anyLabelsVisible
    ? labeledMargins
    : {
        top: CONTENT_PADDING,
        right: CONTENT_PADDING,
        bottom: CONTENT_PADDING,
        left: CONTENT_PADDING,
        ...margin,
      };
  const width = Math.max(chartWidth - (margins.left + margins.right), 1);
  const radius = Math.max(width / 2, 1);
  const cx = width / 2;
  const contentBounds = radarContentBounds(axes, radius, {
    includeAxisLabel: anyLabelsVisible ? showAxisLabel : () => false,
  });
  const height = Math.max(contentBounds.maxY - contentBounds.minY, 1);
  const cy = -contentBounds.minY;
  const chartHeight = height + margins.top + margins.bottom;
  const usePolygonGrid = axisCount >= 3;

  const gridPaths = useMemo(
    () =>
      GRID_TICKS.map((tick) =>
        usePolygonGrid
          ? pointsToPath(
              polygonPoints(cx, cy, scoreToRadius(tick, radius), axisCount)
            )
          : null
      ),
    [cx, cy, radius, axisCount, usePolygonGrid]
  );

  const axisLines = useMemo(
    () =>
      Array.from({ length: axisCount }, (_, i) => {
        const end = polarPoint(cx, cy, radius, axisAngle(i, axisCount));
        return { x2: end.x, y2: end.y };
      }),
    [axisCount, cx, cy, radius]
  );

  const boundarySegments = useMemo(() => {
    if (axisCount < 2) {
      return [];
    }
    const outer = polygonPoints(cx, cy, radius, axisCount);
    return outer.map((vertex, i) => {
      const prev = outer[(i - 1 + axisCount) % axisCount];
      const next = outer[(i + 1) % axisCount];
      return {
        key: `${axes[i].key}-boundary`,
        paletteClassName: axes[i].paletteClassName,
        dummy: Boolean(axes[i].dummy || axes[i].kind === 'dummy'),
        d: cornerBoundaryPath(prev, vertex, next),
      };
    });
  }, [axes, axisCount, cx, cy, radius]);

  const categoryFills = useMemo(
    () => categoryBackgroundSectors(axes, cx, cy, radius),
    [axes, cx, cy, radius]
  );

  const labelPathPrefix = useMemo(() => uniqueId('radar-label-path-'), []);

  const labelRadius = radius + LABEL_RADIUS_OFFSET;
  const labelItems = useMemo(
    () =>
      axes.map((axis, i) => {
        const angle = axisAngle(i, axisCount);
        const halfSpan = labelArcHalfSpan(axis.label, labelRadius);
        return {
          ...axis,
          angle,
          lower: isLowerHalf(angle),
          pathId: `${labelPathPrefix}${i}`,
          d: curvedLabelPath(cx, cy, labelRadius, angle, halfSpan),
          showLabel:
            anyLabelsVisible && !isDummyAxis(axis) && Boolean(axis?.label),
        };
      }),
    [anyLabelsVisible, axes, axisCount, cx, cy, labelRadius, labelPathPrefix]
  );

  const renderedSeries = useMemo(() => {
    return series.map((item) => {
      const active = activeSeriesKey == null || item.key === activeSeriesKey;
      const filled = filledSeriesKey != null && item.key === filledSeriesKey;
      return {
        ...item,
        points: seriesPoints(item.values, cx, cy, radius, axisCount),
        active,
        filled,
        paletteClassName: active
          ? item.paletteClassName
          : item.mutedPaletteClassName ?? item.paletteClassName,
      };
    });
  }, [series, cx, cy, radius, axisCount, activeSeriesKey, filledSeriesKey]);

  const inactiveSeries = renderedSeries
    .filter((item) => !item.active)
    .reverse();
  const activeSeries = renderedSeries.filter((item) => item.active);
  const activePunchMaskId = useMemo(
    () => uniqueId('radar-active-punch-mask-'),
    []
  );
  const chartSize = { width, height };
  const activePunchHoles = activeSeries.flatMap((item) =>
    markerHoles(item, axes)
  );
  const overlayPoints = useMemo(
    () => renderedSeries.flatMap((item) => scoredMarkers(item, axes)),
    [renderedSeries, axes]
  );
  const hoverableSeries = useMemo(() => {
    if (lockedSeriesKey == null) {
      return renderedSeries;
    }
    return renderedSeries.filter((item) => item.key === lockedSeriesKey);
  }, [renderedSeries, lockedSeriesKey]);
  const hoverablePoints = useMemo(
    () => hoverableSeries.flatMap((item) => scoredMarkers(item, axes)),
    [hoverableSeries, axes]
  );
  const {
    tooltipData,
    tooltipLeft,
    tooltipTop,
    tooltipOpen,
    showTooltip,
    hideTooltip,
  } = useTooltip();

  const pointFromEvent = useCallback(
    (event) => {
      const coords = localPoint(event.target.ownerSVGElement, event);
      if (!coords) {
        return null;
      }
      return {
        svgX: coords.x,
        svgY: coords.y,
        x: coords.x - margins.left,
        y: coords.y - margins.top,
      };
    },
    [margins.left, margins.top]
  );

  const handleNearestMove = useCallback(
    (event) => {
      const point = pointFromEvent(event);
      if (!point) {
        return;
      }
      const marker = findClosestRadarMarker(hoverablePoints, point.x, point.y, {
        maxDistance: TOOLTIP_HIT_RADIUS,
      });
      if (marker) {
        onHoverSeries(marker.seriesKey);
        showTooltip({
          tooltipLeft: point.svgX,
          tooltipTop: point.svgY,
          tooltipData: marker,
        });
        return;
      }
      hideTooltip();
      const segment = findClosestRadarSegment(
        hoverableSeries,
        point.x,
        point.y
      );
      if (segment) {
        onHoverSeries(segment.seriesKey);
      } else {
        onClearSeries();
      }
    },
    [
      hoverablePoints,
      hoverableSeries,
      pointFromEvent,
      onHoverSeries,
      onClearSeries,
      showTooltip,
      hideTooltip,
    ]
  );

  const handleNearestLeave = useCallback(() => {
    hideTooltip();
    onClearSeries();
  }, [hideTooltip, onClearSeries]);

  const handleNearestClick = useCallback(
    (event) => {
      const point = pointFromEvent(event);
      if (!point) {
        return;
      }
      const marker = findClosestRadarMarker(overlayPoints, point.x, point.y, {
        maxDistance: TOOLTIP_HIT_RADIUS,
      });
      if (marker) {
        onClickSeries(marker.seriesKey);
        return;
      }
      const segment = findClosestRadarSegment(renderedSeries, point.x, point.y);
      if (segment) {
        onClickSeries(segment.seriesKey);
      }
    },
    [overlayPoints, renderedSeries, pointFromEvent, onClickSeries]
  );

  if (axisCount === 0) {
    return null;
  }

  return (
    <div className="radar-chart-frame">
      <svg
        viewBox={`0 0 ${chartWidth} ${chartHeight}`}
        className={cnx('radar-chart', dimInactive && 'dim-inactive')}
        role="img"
        aria-label="Assessment radar chart"
        xmlnsXlink="http://www.w3.org/1999/xlink"
      >
        <g transform={`translate(${margins.left},${margins.top})`}>
          <g className="radar-category-fills">
            {categoryFills.map((fill) => (
              <path
                key={fill.key}
                d={fill.d}
                className={cnx('radar-category-fill', fill.paletteClassName)}
              />
            ))}
          </g>
          <g className="radar-grid">
            {usePolygonGrid
              ? gridPaths.map((d, i) => (
                  <path key={`grid-${i}`} d={d} className="chart-grid-dense" />
                ))
              : GRID_TICKS.map((tick) => (
                  <circle
                    key={`grid-${tick}`}
                    cx={cx}
                    cy={cy}
                    r={scoreToRadius(tick, radius)}
                    className="chart-grid-dense"
                  />
                ))}
            {axisLines.map((line, i) => (
              <line
                key={`axis-${i}`}
                x1={cx}
                y1={cy}
                x2={line.x2}
                y2={line.y2}
                className="wedge-divider inner-divider"
              />
            ))}
          </g>

          <g className="radar-boundary">
            {boundarySegments.map((segment) => (
              <path
                key={segment.key}
                d={segment.d}
                className={cnx(
                  'radar-boundary-segment',
                  'stroke-only',
                  segment.dummy && 'is-dummy',
                  !segment.dummy && segment.paletteClassName
                )}
              />
            ))}
          </g>

          <g className="radar-series">
            {activePunchHoles.length > 0 ? (
              <defs>
                <mask
                  id={activePunchMaskId}
                  maskUnits="userSpaceOnUse"
                  maskContentUnits="userSpaceOnUse"
                >
                  <rect {...layerMaskRect(chartSize)} fill="white" />
                  {activePunchHoles.map((point, i) => (
                    <circle
                      key={`active-punch-${i}`}
                      cx={point.x}
                      cy={point.y}
                      r={ACTIVE_MARKER.rSpacer}
                      fill={ACTIVE_PUNCH_MASK_FILL}
                    />
                  ))}
                </mask>
              </defs>
            ) : null}
            <g
              className="radar-series-inactive"
              mask={
                activePunchHoles.length > 0
                  ? `url(#${activePunchMaskId})`
                  : undefined
              }
            >
              {inactiveSeries.map((item) => (
                <RadarSeriesItem
                  key={item.key}
                  item={item}
                  axes={axes}
                  axisCount={axisCount}
                  chartSize={chartSize}
                />
              ))}
            </g>
            <g className="radar-series-active">
              {activeSeries.map((item) => (
                <RadarSeriesItem
                  key={item.key}
                  item={item}
                  axes={axes}
                  axisCount={axisCount}
                  chartSize={chartSize}
                />
              ))}
            </g>
          </g>

          <g className="radar-labels">
            {anyLabelsVisible ? (
              <>
                <defs>
                  {labelItems
                    .filter((label) => label.showLabel)
                    .map((label) => (
                      <path
                        key={label.pathId}
                        id={label.pathId}
                        d={label.d}
                        fill="none"
                      />
                    ))}
                </defs>
                {labelItems
                  .filter((label) => label.showLabel)
                  .map((label) => (
                    <text
                      key={label.key}
                      className="radar-axis-label"
                      dy={label.lower ? '0.85em' : '-0.35em'}
                    >
                      <title>{label.label}</title>
                      <textPath
                        href={`#${label.pathId}`}
                        xlinkHref={`#${label.pathId}`}
                        startOffset="50%"
                        textAnchor="middle"
                      >
                        {truncateLabel(label.label)}
                      </textPath>
                    </text>
                  ))}
              </>
            ) : null}
          </g>
          <rect
            className="radar-nearest-layer"
            {...layerMaskRect(chartSize)}
            onMouseMove={handleNearestMove}
            onMouseLeave={handleNearestLeave}
            onClick={handleNearestClick}
          />
        </g>
      </svg>
      <RadarNodeTooltip
        tooltipOpen={tooltipOpen}
        tooltipData={tooltipData}
        tooltipLeft={tooltipLeft}
        tooltipTop={tooltipTop}
      />
    </div>
  );
};

export default withParentSize(RadarChart);
